import React, { useEffect, useState } from "react";
import { ModelInfo } from "@jammable/clientapi";
import VoiceImageJs from "../../../../../apps/web/components/VoiceImage";

/* eslint-disable @typescript-eslint/no-explicit-any */

// VoiceImage is untyped JS whose props all read as required; every one is
// optional in practice.
export const VoiceImage = VoiceImageJs as unknown as React.FC<any>;

type NamedModel = Pick<ModelInfo, "artistname"> & Partial<ModelInfo>;

export function joinArtistNames(
  model?: { artistname?: string } | null,
  models?: NamedModel[] | null,
): string {
  if (models && models.length > 0) {
    const names = models.map((m) => m.artistname);
    if (names.length > 1) {
      return names.slice(0, -1).join(", ") + " & " + names[names.length - 1];
    }
    return names[0] ?? "";
  }
  return model?.artistname ?? "";
}

// Same destination the old "Reuse Voices" buttons used: a single voice goes
// to its page, several go to the studio pre-filled.
export function reuseVoicesHref(
  models?: Partial<ModelInfo>[] | null,
  model?: { urlParam?: string } | null,
): string {
  if (!models || models.length === 0) {
    return model?.urlParam ? `/${model.urlParam}` : "/models";
  }
  if (models.length === 1) return `/${models[0]?.urlParam}`;
  return `/create?voices=${models.map((m) => m._id).join(",")}`;
}

export function timeAgo(date?: string | Date | null): string | null {
  if (!date) return null;
  const then = new Date(date).getTime();
  if (Number.isNaN(then)) return null;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} ${days === 1 ? "day" : "days"} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} ${months === 1 ? "month" : "months"} ago`;
  const years = Math.round(days / 365);
  return `${years} ${years === 1 ? "year" : "years"} ago`;
}

export interface TranscriptionSegment {
  start: number;
  end: number;
  text: string;
  words?: any[];
}

export function useTranscription(url?: string | null) {
  const [transcription, setTranscription] = useState<
    TranscriptionSegment[] | null
  >(null);

  useEffect(() => {
    const fetchTranscription = async () => {
      if (url) {
        try {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          // The transcription can be an array directly or nested under a "segments" property
          if (Array.isArray(data)) {
            setTranscription(data);
          } else if (data && Array.isArray(data.segments)) {
            setTranscription(data.segments);
          }
        } catch (error) {
          console.error("Failed to fetch transcription:", error);
        }
      }
    };

    fetchTranscription();
  }, [url]);

  return transcription;
}

// Index of the lyric line being sung at `seconds`: the segment that contains
// it, else the last one that has started (so the card holds between lines).
export function activeLineAt(
  transcription: TranscriptionSegment[] | null,
  seconds: number,
): number {
  if (!transcription || seconds <= 0) return -1;
  let idx = -1;
  for (let i = 0; i < transcription.length; i++) {
    const seg = transcription[i];
    if (seg.start <= seconds) idx = i;
    else break;
  }
  return idx;
}
